import React, { useState } from 'react';
import browserGuardService from '../services/browserGuard';
import { startBlackjackGame, blackjackAction } from '../services/api';
import './Casino.css';

const CHIPS = [
  { value: 1,   label: '1',   color: '#e8e8e8', border: '#999', text: '#333' },
  { value: 5,   label: '5',   color: '#c0392b', border: '#922b21', text: '#fff' },
  { value: 25,  label: '25',  color: '#27ae60', border: '#1e8449', text: '#fff' },
  { value: 50,  label: '50',  color: '#2980b9', border: '#1a5276', text: '#fff' },
  { value: 100, label: '100', color: '#2c2c2c', border: '#111', text: '#fff' },
  { value: 500, label: '500', color: '#7d3c98', border: '#5b2c6f', text: '#fff' },
];

function Chip({ chip, onClick, disabled, size = 'lg' }) {
  return (
    <button
      className={`chip chip-${size}`}
      style={{
        background: chip.color,
        borderColor: chip.border,
        color: chip.text,
      }}
      onClick={() => onClick && onClick(chip.value)}
      disabled={disabled}
      title={`$${chip.value}`}
    >
      <span className="chip-label">{chip.label}</span>
    </button>
  );
}

function CardDisplay({ card, hidden }) {
  if (hidden) {
    return (
      <div className="card card-back">
        <div className="card-back-pattern" />
      </div>
    );
  }
  const isRed = card.suit === '♥' || card.suit === '♦';
  return (
    <div className={`card ${isRed ? 'card-red' : 'card-black'}`}>
      <div className="card-corner card-corner-tl">
        <div className="card-corner-value">{card.value}</div>
        <div className="card-corner-suit">{card.suit}</div>
      </div>
      <div className="card-center-suit">{card.suit}</div>
      <div className="card-corner card-corner-br">
        <div className="card-corner-value">{card.value}</div>
        <div className="card-corner-suit">{card.suit}</div>
      </div>
    </div>
  );
}

function Casino({ balance, onBalanceChange, setGeoError, buildGeoError, geoVerified }) {
  const [wager, setWager] = useState(0);
  const [gameState, setGameState] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const selectedChip = CHIPS.slice().reverse().find(c => c.value <= wager) || CHIPS[0];

  const addChip = (value) => {
    setWager(prev => prev + value);
    setMessage(null);
  };

  const clearWager = () => {
    setWager(0);
    setMessage(null);
  };

  const allIn = () => {
    setWager(balance);
    setMessage(null);
  };

  const handleDeal = async () => {
    if (!geoVerified) {
      setMessage({ text: 'Location not verified — a passing GeoComply check is required to play.', type: 'error' });
      return;
    }
    if (!wager || wager <= 0) {
      setMessage({ text: 'Place a bet first.', type: 'error' });
      return;
    }
    if (wager > balance) {
      setMessage({ text: 'Insufficient balance.', type: 'error' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await startBlackjackGame(wager);
      if (response.success) {
        browserGuardService.logFinancialTransaction('Blackjack Deal');
        setGameState(response.game);
        onBalanceChange(response.newBalance);
        if (response.game.status !== 'playing') {
          setMessage({ text: response.game.result, type: resultType(response.game.status) });
        }
      } else {
        setMessage({ text: response.message || 'Failed to start game.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.message || 'Error starting game.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    if (!gameState) return;
    if (!geoVerified) {
      setMessage({ text: 'Location not verified — checks must pass to continue playing.', type: 'error' });
      return;
    }
    setActionLoading(true);
    try {
      const response = await blackjackAction(gameState.gameId, action);
      if (response.success) {
        setGameState(response.game);
        onBalanceChange(response.newBalance);
        if (response.game.status !== 'playing') {
          setMessage({ text: response.game.result, type: resultType(response.game.status) });
        }
      } else {
        setMessage({ text: response.message || 'Action failed.', type: 'error' });
      }
    } catch (err) {
      setMessage({ text: err.response?.data?.message || 'Error.', type: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleNewGame = () => {
    setGameState(null);
    setMessage(null);
    setWager(0);
  };

  function resultType(status) {
    if (status === 'player_win' || status === 'blackjack') return 'win';
    if (status === 'push') return 'push';
    return 'lose';
  }

  const isPlaying = gameState && gameState.status === 'playing';
  const isFinished = gameState && gameState.status !== 'playing';

  return (
    <div className="bj-root">
      {/* Felt Table */}
      <div className="bj-table">
        {/* --- LOBBY (no active game) --- */}
        {!gameState && (
          <div className="bj-lobby">
            {/* Wager display center */}
            <div className="bj-wager-display">
              {wager > 0 ? (
                <>
                  <Chip chip={selectedChip} size="md" />
                  <span className="bj-wager-amount">${wager.toFixed(2)}</span>
                  <button className="bj-clear-btn" onClick={clearWager}>✕</button>
                </>
              ) : (
                <span className="bj-wager-placeholder">Place your bet</span>
              )}
            </div>

            {!geoVerified && (
              <div className="bj-geo-lock">🔒 Location not verified — a passing GeoComply check is required to play.</div>
            )}

            {/* Deal button */}
            <button
              className="bj-deal-btn"
              onClick={handleDeal}
              disabled={loading || wager <= 0 || !geoVerified}
              title={geoVerified ? 'Deal' : 'Location must be verified first'}
            >
              {loading ? '...' : !geoVerified ? 'LOCKED' : 'DEAL'}
            </button>

            {message && (
              <div className={`bj-message bj-message-${message.type}`}>{message.text}</div>
            )}
          </div>
        )}

        {/* --- ACTIVE GAME --- */}
        {gameState && (
          <div className="bj-game">
            {/* Dealer area */}
            <div className="bj-hand-area bj-dealer-area">
              <div className="bj-hand-label">
                Dealer
                <span className="bj-hand-value">
                  {isPlaying ? gameState.dealerVisibleValue : gameState.dealerValue}
                </span>
              </div>
              <div className="bj-cards">
                {gameState.dealerHand.map((card, i) => (
                  <CardDisplay key={i} card={card} hidden={isPlaying && i === 1} />
                ))}
              </div>
            </div>

            {/* Result banner */}
            {message && (
              <div className={`bj-result bj-result-${message.type}`}>
                {message.text}
                {isFinished && gameState.payout > 0 && (
                  <span className="bj-payout"> +${gameState.payout.toFixed(2)}</span>
                )}
              </div>
            )}

            {/* Player area */}
            <div className="bj-hand-area bj-player-area">
              <div className="bj-cards">
                {gameState.playerHand.map((card, i) => (
                  <CardDisplay key={i} card={card} hidden={false} />
                ))}
              </div>
              <div className="bj-hand-label">
                You
                <span className={`bj-hand-value ${gameState.playerValue > 21 ? 'bj-bust' : gameState.playerValue === 21 ? 'bj-bj' : ''}`}>
                  {gameState.playerValue}
                </span>
              </div>
            </div>

            {/* Action buttons */}
            {isPlaying && (
              <div className="bj-actions">
                <button className="bj-action-btn bj-hit" onClick={() => handleAction('hit')} disabled={actionLoading}>
                  HIT
                </button>
                <button className="bj-action-btn bj-stand" onClick={() => handleAction('stand')} disabled={actionLoading}>
                  STAND
                </button>
              </div>
            )}

            {isFinished && (
              <button className="bj-deal-btn bj-newgame-btn" onClick={handleNewGame}>
                NEW GAME
              </button>
            )}

            <div className="bj-wager-badge">
              Bet: <strong>${gameState.wager?.toFixed(2)}</strong>
            </div>
          </div>
        )}
      </div>

      {/* Bottom panel — chips + bank */}
      {!gameState && (
        <div className="bj-bottom-panel">
          {/* Bank info */}
          <div className="bj-bank">
            <div className="bj-bank-label">Bank: <strong>${balance.toFixed(2)}</strong></div>
            <button className="bj-allin-btn" onClick={allIn} disabled={loading}>ALL IN</button>
          </div>

          {/* Chip tray */}
          <div className="bj-chip-tray">
            {CHIPS.map(chip => (
              <Chip
                key={chip.value}
                chip={chip}
                onClick={addChip}
                disabled={loading || chip.value > balance}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Casino;
